/**
 * apiFetch — a thin wrapper around fetch() that attaches a CRM-audience Entra
 * access token to every request and surfaces server errors as thrown Error
 * objects with the response status attached.
 *
 * LIFTED for the unified shell (was customer-management's own MSAL): the token
 * now comes from the shell's scope-parameterized acquireToken(getCrmScopes())
 * seam (the customer-management-api validates the CRM audience), and the URL is
 * rewritten via crmUrl() to the /v1/crm/* base. The 27 call sites keep their
 * original '/api/...' path strings.
 *
 * SECURITY: the per-customer IDOR boundary is SERVER-SIDE in the
 * customer-management-api (verified, 48-case suite). This client just calls the
 * API — it does NOT (and must not) re-implement that check.
 *
 * Call as:
 *   const data = await apiFetch<{ notes: Note[] }>('/api/notes?customer_id=X');
 */

import { acquireToken, needsInteractiveSignIn } from '@/auth/msal';
import { crmUrl, getCrmScopes } from '@/features/crm/crm-config';

/**
 * What the user sees when a response carries no `error` field of its own.
 *
 * This used to be `` `${status} ${statusText}` ``, and on 2026-08-12 an HSP user
 * uploading an agreement on staging got a banner reading, in full: `500`.
 *
 * TWO things combined. The route threw without a handler, so Hono answered with a
 * plain-text body and there was no `error` to show — and `statusText` is **always
 * the empty string over HTTP/2**, which is what Azure Container Apps serves. So
 * the template produced `"500 "`, and the trailing space is the only thing
 * separating it from a naked number.
 *
 * That is not this one route's problem: EVERY CRM surface funnels through here, so
 * any bodyless error anywhere renders as a bare status code. A number tells the
 * reader nothing about whether to retry, wait, or report — so this says which of
 * those it is, and keeps the code as the thing to quote.
 */
export function fallbackMessage(status: number, statusText: string): string {
  // Only include statusText when the transport actually supplied one (HTTP/1.1).
  const code = statusText.trim()
    ? `${status} ${statusText.trim()}`
    : `${status}`;
  if (status === 404) {
    return `That wasn’t found (${code}). It may have been renamed or removed.`;
  }
  if (status === 403) {
    return `You don’t have access to that (${code}).`;
  }
  if (status === 409) {
    return `That conflicts with the current state (${code}) — reload and try again.`;
  }
  if (status === 413) {
    return `That was too large to accept (${code}).`;
  }
  if (status === 429) {
    return `Too many requests (${code}) — wait a moment and try again.`;
  }
  if (status >= 500) {
    return (
      `The server could not complete that request (${code}) and did not say why. ` +
      `This is a problem on our side — please report it with what you were doing.`
    );
  }
  if (status >= 400) {
    return `That request was rejected (${code}) and no reason was given.`;
  }
  return `The request failed (${code}).`;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function apiFetch<T = unknown>(
  url: string,
  init: RequestInit = {},
): Promise<T> {
  // CRM-audience token via the scope-parameterized seam (NOT the parameterless
  // form). undefined → no token; mirror the wrapper and never send unauthenticated.
  const token = await acquireToken(getCrmScopes());
  // Refuse to send rather than firing a request guaranteed to 401.
  //
  // This used to be `if (token) headers.set(...)`, which meant a missing token
  // produced a real HTTP call whose response — "Missing or malformed
  // Authorization header" — described the SYMPTOM and pointed the reader at the
  // server. On 2026-07-31 that message was the only visible evidence of a
  // whole-CRM-surface auth failure in staging and prod, and it sent the
  // investigation to the wrong layer.
  //
  // Every endpoint reached through this helper is an authenticated CRM route
  // (/api/me, /api/customers, /api/admin/*, /api/btp/*, ...) — there is no
  // public one — so no token means the call cannot succeed. Failing here is
  // both cheaper and honest about where the problem is. acquireToken has
  // already reported WHY via captureAuthAnomaly.
  if (!token) {
    // Distinguish the two failures, because the remedies differ and only one of
    // them is the user's to perform.
    //
    // The 2026-07-31 investigation found the real-world case is
    // `refresh_token_expired` — an ordinary expiry with no interactive fallback,
    // which presented as a permanent, inexplicable 401 across the whole app. A
    // generic message sent that investigation to the server for hours.
    if (needsInteractiveSignIn()) {
      throw new ApiError(
        'Your sign-in has expired. Sign in again to continue — nothing has been lost.',
        401,
        { error: 'reauth_required' },
      );
    }
    throw new ApiError(
      'Not signed in to the Customer Management API — your session could not be verified. If this persists, the crm scope grant needs checking.',
      401,
      { error: 'no_crm_token' },
    );
  }
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const resp = await fetch(crmUrl(url), { ...init, headers });

  if (!resp.ok) {
    // Read the body as text once, then try JSON. resp.json() and resp.text()
    // can each only consume the body stream a single time.
    const text = await resp.text();
    let body: unknown = text;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      // body stays as the raw text
    }
    const message =
      typeof body === 'object' &&
      body !== null &&
      'error' in body &&
      typeof (body as { error: unknown }).error === 'string'
        ? (body as { error: string }).error
        : fallbackMessage(resp.status, resp.statusText);
    throw new ApiError(message, resp.status, body);
  }

  if (resp.status === 204) return undefined as T;

  // Parse the success body OURSELVES rather than calling resp.json(), so a 2xx
  // that is not JSON becomes an ApiError like every other failure instead of a
  // bare SyntaxError.
  //
  // This is not hypothetical. It happens whenever something between the browser
  // and the API answers on the API's behalf — an edge redirect that lands in the
  // SPA, a proxy serving index.html, a rewrite rule that eats the path. The
  // request comes back 200 text/html; status 200 is `ok`, so the error path
  // above is skipped and resp.json() throws "Unexpected token '<'" from inside
  // the data layer — a message that names nothing, points at no layer, and
  // leaves panels blank with no explanation. Diagnosed once in August 2026 from
  // a misrouted host, and the class of failure outlives any one cause: a
  // misroute between the browser and the API is exactly the thing this helper
  // should be able to describe, in the same shape as everything else.
  const text = await resp.text();
  try {
    return JSON.parse(text) as T;
  } catch {
    const looksLikeHtml = /^\s*(?:<!doctype|<html)/i.test(text);
    throw new ApiError(
      looksLikeHtml
        ? 'The Customer Management API returned a web page instead of data. The request was answered by the app, not the API — check the address you are using (the canonical host is app.andavolabs.com/crm).'
        : 'The Customer Management API returned a response that could not be read as data.',
      resp.status,
      {
        error: 'non_json_response',
        contentType: resp.headers.get('content-type'),
        preview: text.slice(0, 200),
      },
    );
  }
}
